import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { ChancenComponentSemanticEntityPicker } from '@chancenwerk/client/components/semantics/chancen-component-semantic-entity-picker.js';
import { projectConfig } from '@chancenwerk/config.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { customElement, property } from 'lit/decorators.js';
import { RaumRoutes } from '../../../../chancenraum/src/client/routes.js';
/**
 * @customElement chancen-component-semantic-user-picker
 */
@customElement('chancen-component-semantic-user-picker')
export class ChancenComponentSemanticUserPicker extends ChancenComponentSemanticEntityPicker<
  ChancenwerkUser,
  ChancenwerkUserFilter
> {
  @property({ attribute: false }) public routeToNewEntity = RaumRoutes.editPerson;
  protected override _permission = [
    ChancenPermission.raum.CREATE_EMPLOYEE,
    ChancenPermission.raum.EDIT_STUDENT,
    ChancenPermission.raum.EDIT_EMPLOYEE,
    ChancenPermission.raum.EDIT_EXTERNAL_CONTACT_PERSON,
  ];
  override placeholder: string = 'Person';

  protected override _subscription() {
    return UserAPI.subscribeByFilter;
  }

  protected override _resolveID(
    id: string,
  ): (gqlFields: BaseQLSelectionSet<ChancenwerkUser>) => Promise<Maybe<ChancenwerkUser>> {
    return getByID(ChancenwerkUser, id);
  }

  protected override _filter() {
    return ChancenwerkUserFilter;
  }

  protected override _selectionSet() {
    return { vorname: 1, nachname: 1, email: 1 };
  }

  private _getRouteToUserType(user?: Maybe<ChancenwerkUser>) {
    if (!user) return this.routeToNewEntity;
    return user instanceof Mitarbeiter
      ? RaumRoutes.editEmployee
      : user instanceof Student
      ? RaumRoutes.editStudent
      : RaumRoutes.editPerson;
  }

  protected override _controlEdit(entity: ChancenwerkUser): void {
    goTo(`${projectConfig.get('RAUM_URL').slice(0, -1)}${this._getRouteToUserType(entity)}/${entity._id}`, {
      target: '_blank',
    });
  }

  protected override _controlNew(): void {
    goTo(`${projectConfig.get('RAUM_URL').slice(0, -1)}${this._getRouteToUserType()}/new`, { target: '_blank' });
  }
}
